import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { useCreateStripeAccount } from '../api/stripeAccounts';
import { Field } from '../components/Field';

const SLUG_RE = /^[a-z0-9-]+$/;
const SK_RE = /^sk_(test|live)_[A-Za-z0-9]+$/;
const WHSEC_RE = /^whsec_[A-Za-z0-9]+$/;
const PK_RE = /^pk_(test|live)_[A-Za-z0-9]+$/;
const ACCT_RE = /^acct_[A-Za-z0-9]+$/;

const createSchema = z.object({
  label: z.string().min(1).max(64).regex(SLUG_RE, 'lowercase letters, digits, hyphens'),
  displayName: z.string().min(1).max(120),
  secretKey: z.string().regex(SK_RE, 'must start with sk_test_ or sk_live_'),
  webhookSecret: z.string().regex(WHSEC_RE, 'must start with whsec_'),
  publishableKey: z.string().regex(PK_RE, 'must start with pk_test_ or pk_live_'),
  stripeAccountId: z
    .string()
    .refine((s) => s === '' || ACCT_RE.test(s), { message: 'must match acct_xxx' }),
});

type CreateValues = z.infer<typeof createSchema>;

const ERROR_LABELS: Record<string, string> = {
  stripe_auth_failed: 'Stripe rejected the secret key (authentication failed).',
  stripe_permission_denied: 'The secret key does not have the required permissions.',
  stripe_unreachable: "Couldn't reach Stripe. Try again.",
  stripe_rate_limited: 'Stripe rate-limited the request. Wait a minute and retry.',
  stripe_account_mismatch:
    "The secret key doesn't belong to the Stripe account id you provided.",
  stripe_unknown: 'Stripe returned an unexpected error.',
  label_taken: 'A Stripe account with that label already exists.',
};

export function StripeAccountCreate() {
  const navigate = useNavigate();
  const createAccount = useCreateStripeAccount();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { stripeAccountId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await createAccount.mutateAsync({
        label: values.label,
        displayName: values.displayName,
        secretKey: values.secretKey,
        webhookSecret: values.webhookSecret,
        publishableKey: values.publishableKey,
        stripeAccountId: values.stripeAccountId === '' ? undefined : values.stripeAccountId,
      });
      navigate(`/stripe-accounts/${created.id}/edit`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const msg = ERROR_LABELS[err.code] ?? 'Could not create the account.';
        setError('root', { message: msg });
      } else {
        setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>New Stripe account</h1>
        <div className="page-actions">
          <Link to="/stripe-accounts" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </div>
      <form onSubmit={onSubmit} className="card card-pad">
        <Field
          label="Display name"
          help="Shown in the events list and the account picker."
          error={errors.displayName?.message}
        >
          <input type="text" autoComplete="off" {...register('displayName')} />
        </Field>
        <Field
          label="Label (slug)"
          help="Stable identifier; lowercase letters, digits, hyphens. Used by the break-glass CLI."
          error={errors.label?.message}
        >
          <input type="text" autoComplete="off" {...register('label')} />
        </Field>
        <Field
          label="Stripe account id (optional)"
          help="acct_xxx from Stripe Dashboard. Used for deep-links and validated against the secret key on save."
          error={errors.stripeAccountId?.message}
        >
          <input type="text" autoComplete="off" {...register('stripeAccountId')} />
        </Field>
        <Field
          label="Publishable key"
          help="pk_test_… or pk_live_… — non-secret; sent to mobile devices."
          error={errors.publishableKey?.message}
        >
          <input type="text" autoComplete="off" {...register('publishableKey')} />
        </Field>
        <Field
          label="Secret key"
          help="sk_test_… or sk_live_… — validated against Stripe before saving."
          error={errors.secretKey?.message}
        >
          <input type="password" autoComplete="off" {...register('secretKey')} />
        </Field>
        <Field
          label="Webhook signing secret"
          help="whsec_… — used to verify Stripe webhook payloads."
          error={errors.webhookSecret?.message}
        >
          <input type="password" autoComplete="off" {...register('webhookSecret')} />
        </Field>

        {errors.root && <div className="form-error">{errors.root.message}</div>}
        <div className="page-actions">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create account'}
          </button>
        </div>
      </form>
    </div>
  );
}
